/**
 * Form schema field helpers (for FormSchema type)
 */

import type { FormField, FormSchema, FormStep } from '@core-engine/schema/types.js'

/**
 * Get all fields from a form schema (including from steps), flattening groups
 */
export function getAllFormFields(schema: FormSchema): FormField[] {
  const allFields: FormField[] = []

  const sources = schema.steps ? schema.steps.flatMap((step) => step.fields) : schema.fields || []

  for (const field of sources) {
    if (field.type === 'group' && field.fields) {
      for (const nestedField of field.fields) {
        allFields.push({
          ...nestedField,
          condition: nestedField.condition || field.condition
        })
      }
    } else {
      allFields.push(field)
    }
  }

  return allFields
}

/**
 * Check if a form schema is multi-step
 */
export function isMultiStepForm(schema: FormSchema): boolean {
  return Array.isArray(schema.steps) && schema.steps.length > 0
}

/**
 * Get all concrete fields from a form schema, flattening groups and skipping dynamicFields.
 * Works with both flat schemas (schema.fields) and multi-step schemas (schema.steps).
 */
export function getAllFormSchemaFields(schema: FormSchema): FormField[] {
  const flatten = (fields: FormField[]): FormField[] => {
    return fields.flatMap((field) => {
      if (field.type === 'dynamicFields') return []
      if (field.type === 'group' && field.fields) return flatten(field.fields)
      return field.type === 'group' ? [] : [field]
    })
  }
  if (schema.fields) return flatten(schema.fields)
  if (schema.steps) return schema.steps.flatMap((step) => flatten(step.fields))
  return []
}

/**
 * Get flat array of field names for a form step (for RHF trigger validation).
 * Groups are flattened to child names, list fields use parent name,
 * hidden and dynamicFields are excluded, showWhen fields are included.
 */
export function getStepFieldNames(step: FormStep): string[] {
  const names: string[] = []
  const collect = (fields: FormField[]): void => {
    for (const field of fields) {
      if (field.hidden) continue
      if (field.type === 'dynamicFields') continue
      if (field.type === 'group' && field.fields) {
        collect(field.fields)
      } else if (field.name) {
        names.push(field.name)
      }
    }
  }
  collect(step.fields)
  return names
}

/**
 * Check if a form schema contains dynamicFields
 */
export function hasDynamicFields(schema: FormSchema): boolean {
  const check = (fields: FormField[]): boolean => {
    for (const f of fields) {
      if (f.type === 'dynamicFields') return true
      if (f.type === 'group' && f.fields && check(f.fields)) return true
    }
    return false
  }
  if (schema.fields && check(schema.fields)) return true
  if (schema.steps) {
    for (const step of schema.steps) {
      if (check(step.fields)) return true
    }
  }
  return false
}
